import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { getHrmsBranchList } from "../../../Redux/action/Organisation/Branch/BranchAction";
import { getRoleView } from "../../../Redux/action/HRMS/Login/LoginMasterAction";
import { Table, Tooltip } from "antd";
import { BeatLoader } from "react-spinners";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { adminMainUrl } from "../../../Config";
import AuditDataViewer from "./AuditDataViewer";

const Pagination = ({ totalRecords, perPage, currentPage, onPageChange }) => {
  const totalPages = Math.ceil(totalRecords / perPage);
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    });
    return rangeWithDots;
  };

  return (
    <div className="pagination-wrapper">
      <button
        className="pagination-btn prev-next"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        &lt;
      </button>

      {getPageNumbers().map((page, idx) =>
        page === "..." ? (
          <span key={idx} className="pagination-dots">...</span>
        ) : (
          <button
            key={idx}
            className={`pagination-btn ${page === currentPage ? "active" : ""}`}
            onClick={() => onPageChange(page)}
          >
            {page}
          </button>
        )
      )}

      <button
        className="pagination-btn prev-next"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        &gt;
      </button>

      <style>{`
        .pagination-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          margin-top: 30px;
          margin-bottom: 20px;
        }
        .pagination-btn {
          min-width: 36px;
          height: 36px;
          padding: 0 12px;
          border: 1px solid #dee2e6;
          background: white;
          color: #6c757d;
          font-size: 14px;
          font-weight: 500;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .pagination-btn:hover:not(:disabled) {
          background-color: #e9ecef;
          border-color: #dee2e6;
          color: #5156be;
        }
        .pagination-btn.active {
          background-color: #5156be;
          border-color: #5156be;
          color: white;
          cursor: default;
        }
        .pagination-btn.prev-next {
          padding: 0 12px;
          font-size: 16px;
          font-weight: bold;
        }
        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .pagination-dots {
          color: #6c757d;
          padding: 0 4px;
        }
      `}</style>
    </div>
  );
};

const AuditLogs = () => {
  const [logsData, setLogsData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [filterBranchId, setFilterBranchId] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");

  const dispatch = useDispatch();

  let { sidebarPermissionHRMSData } = useSelector((state) => state?.NavbarReducer);
  const hasFilterPermission = sidebarPermissionHRMSData?.role_id == sidebarPermissionHRMSData?.user_id;

  let { HrmsBranchList } = useSelector(state => state?.BranchReducer);
  const branchList = [
    { value: "", label: "All" },
    ...(HrmsBranchList && Array.isArray(HrmsBranchList)
      ? HrmsBranchList.map((item) => ({
        value: item.branch_id,
        label: item.branch_name,
      }))
      : []),
  ];

  let roleData = useSelector(state => state?.LoginMasterReducer ? state?.LoginMasterReducer?.roleViewData : []);
  const roleOptions = [
    { value: "", label: "All" },
    ...(roleData && Array.isArray(roleData)
      ? roleData.map(val => ({
        value: val.name,
        label: val.name.charAt(0).toUpperCase() + val.name.slice(1),
      }))
      : []),
  ];

  const userId = Cookies.get("flexichain_user_id");

  useEffect(() => {
    const token = Cookies.get("token");
    dispatch(getHrmsBranchList(token, userId));
    dispatch(getRoleView());
  }, [dispatch, userId]);

  const fetchAuditLogs = async (page = 1, isClear = false) => {
    setLoading(true);
    try {
      let urlStr = `${adminMainUrl}/api/auditlogs?page=${page}&limit=${perPage}`;
      if (!isClear) {
        if (filterBranchId) urlStr += `&branch_id=${filterBranchId}`;
        if (filterDateFrom) urlStr += `&date_from=${filterDateFrom}`;
        if (filterDateTo) urlStr += `&date_to=${filterDateTo}`;
        if (filterSearch) urlStr += `&search=${filterSearch}`;
        if (filterRole) urlStr += `&role_name=${filterRole}`;
      }
      const response = await fetch(
        urlStr,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "auth-token": Cookies.get("token"),
            "user_id": userId
          },
        }
      );
      const data = await response.json();
      if (response.ok && data.code === 200) {
        setLogsData(data.data);
        setTotalRecords(data.total_records);
        setPerPage(data.per_page);
        setCurrentPage(data.current_page);
      } else {
        toast.error(data.msg || "Failed to fetch audit logs");
      }
    } catch (error) {
      console.error("Network error:", error);
      toast.error("Network error – could not fetch audit logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs(currentPage);
  }, [currentPage]);

  const handleApplyFilters = () => {
    if (currentPage === 1) {
      fetchAuditLogs(1);
    } else {
      setCurrentPage(1);
    }
  };

  const handleClearFilters = () => {
    setFilterBranchId("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterSearch("");
    setFilterRole("");
    if (currentPage === 1) {
      fetchAuditLogs(1, true);
    } else {
      setCurrentPage(1);
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      let urlStr = `${adminMainUrl}/api/auditlogs?page=1&limit=1000000`;
      if (filterBranchId) urlStr += `&branch_id=${filterBranchId}`;
      if (filterDateFrom) urlStr += `&date_from=${filterDateFrom}`;
      if (filterDateTo) urlStr += `&date_to=${filterDateTo}`;
      if (filterSearch) urlStr += `&search=${filterSearch}`;
      if (filterRole) urlStr += `&role_name=${filterRole}`;
      const response = await fetch(urlStr, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "auth-token": Cookies.get("token"),
          "user_id": userId
        },
      });
      const data = await response.json();
      if (response.ok && data.code === 200) {
        const rows = (data.data || []).map((r, i) => ({
          "S. No.": i + 1,
          "User Id": r.employee_id || r.log_id || "-",
          "User Name": r.user_name || r.user_info?.company_name || "-",
          "User Role": r.role_name || "-",
          "Company": r.company_name || r.user_info?.company_name || "-",
          "Branch": r.branch_name || r.user_info?.branch_name || "-",
          "Module": r.module || "-",
          "Action Type": r.action || "-",
          "Status": r.status || "-",
          // "IP Address": r.ip_address || "-",
          "Created At": r.createdAt ? new Date(r.createdAt).toLocaleString() : "-",
          "Remarks": r.remarks ? r.remarks.replace(/"/g, '""').replace(/(\r\n|\n|\r)/gm, " ") : "-"
        }));

        if (rows.length === 0) {
          toast.warning("No records to export");
          return;
        }

        const headers = Object.keys(rows[0]).join(",");
        const csvString = [
          headers,
          ...rows.map(row => Object.values(row).map(val => `"${val}"`).join(","))
        ].join("\n");

        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `audit_logs_${new Date().getTime()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error(data.msg || "Failed to export data");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error exporting CSV");
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= Math.ceil(totalRecords / perPage)) {
      setCurrentPage(newPage);
    }
  };

  const handleViewDetails = async (id) => {
    setViewLoading(true);
    setViewModalOpen(true);
    try {
      const response = await fetch(`${adminMainUrl}/api/auditlogs/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "auth-token": Cookies.get("token"),
        },
      });
      const data = await response.json();
      if (response.ok && data.code === 200) {
        setViewData(data.data);
      } else {
        toast.error(data.msg || "Failed to fetch details");
        setViewModalOpen(false);
      }
    } catch (error) {
      console.error("Error fetching details:", error);
      toast.error("Network error while fetching details");
      setViewModalOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const closeModal = () => {
    setViewModalOpen(false);
    setViewData(null);
  };

  const columns = [
    {
      title: "S. No.",
      key: "sno",
      width: 75,
      align: "center",
      render: (_, __, index) => (currentPage - 1) * perPage + index + 1,
    },
    {
      title: "User Id",
      dataIndex: "userId",
      key: "userId",
      align: "center",
      width: 80,
      render: (_, records) => {

        let emp_id = records?.employee_id || records?.log_id || "-";

        return (
          <Tooltip title={emp_id || "-"} placement="top">
            <span className="text-truncate d-inline-block" style={{ maxWidth: "125px", verticalAlign: "middle" }}>
              {emp_id || "-"}
            </span>
          </Tooltip>
        )
      }
    },
    {
      title: "User Name",
      dataIndex: "user",
      key: "user",
      width: 180,
      render: (_, records) => {
        let userName = records?.user_name || records.user_info?.company_name || "-";
        return (
          <Tooltip title={userName || "-"} placement="top">
            <span className="text-truncate d-inline-block" style={{ maxWidth: "165px", verticalAlign: "middle" }}>
              {userName || "-"}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: "User Role",
      dataIndex: "userRole",
      key: "userRole",
      width: 180,
      render: (_, records) => {
        let userRole = records?.role_name || "-";
        return (
          <Tooltip title={userRole || "-"} placement="top">
            <span className="text-truncate d-inline-block" style={{ maxWidth: "165px", verticalAlign: "middle" }}>
              {userRole || "-"}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: "Company / Branch",
      key: "company",
      width: 280,
      render: (_, record) => {
        const companyName =
          record.company_name || record.user_info?.company_name || "-";

        const branchName =
          record.branch_name || record.user_info?.branch_name || "-";

        return (
          <Tooltip
            title={
              <div>
                <div>{companyName}</div>
                <div>{branchName}</div>
              </div>
            }
            placement="top"
          >
            <div
              className="fw-medium"
              style={{
                maxWidth: "250px",
                verticalAlign: "middle",
                lineHeight: "1.4",
              }}
            >
              <div className="text-truncate">{companyName}</div>
              <div className="text-truncate text-muted">{branchName}</div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      width: 140,
      render: (text) => (
        <Tooltip title={text || "-"} placement="top">
          <span className="text-truncate d-inline-block" style={{ maxWidth: "125px", verticalAlign: "middle" }}>
            {text || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      align: "center",
      render: (status) => (
        <Tooltip title={`Status: ${status || "-"}`} placement="top">
          <span className={`badge badge-pill ${status === "SUCCESS" ? "bg-success" : "bg-danger"}`}>
            {status || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Action Type",
      dataIndex: "action",
      key: "action",
      width: 180,
      render: (text) => (
        <Tooltip title={text || "-"} placement="top">
          <span className="text-truncate d-inline-block" style={{ maxWidth: "165px", verticalAlign: "middle" }}>
            {text || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 220,
      render: (text) => (
        <Tooltip title={text || "-"} placement="top">
          <span className="text-truncate d-inline-block" style={{ maxWidth: "205px", verticalAlign: "middle" }}>
            {text || "-"}
          </span>
        </Tooltip>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 190,
      render: (date) => {
        const formatted = date ? new Date(date).toLocaleString() : "-";
        return (
          <Tooltip title={formatted} placement="top">
            <span className="text-nowrap">{formatted}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      key: "actionMenu",
      width: 80,
      align: "center",
      render: (_, record) => (
        <div className="dropdown table-action">
          <Tooltip title="Actions" placement="top">
            <Link to="#" className="action-icon" data-bs-toggle="dropdown">
              <i className="fa fa-ellipsis-v"></i>
            </Link>
          </Tooltip>
          <div className="dropdown-menu dropdown-menu-right">
            <div
              className="cursor-pointer dropdown-item"
              onClick={() => handleViewDetails(record.id)}
            >
              <i className="ti ti-eye text-blue-light me-2"></i> View Details
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-8">
                    <h4 className="page-title">
                      Audit Logs
                      <span className="count-title">{totalRecords}</span>
                    </h4>
                  </div>
                  <div className="col-4 text-end">
                    <div className="head-icons d-flex justify-content-end align-items-center gap-2">
                      <button className="btn btn-sm btn-primary" onClick={handleExportCSV} disabled={exportLoading}>
                        {exportLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Exporting...
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-download me-2" />
                            Export CSV
                          </>
                        )}
                      </button>
                      <a href="javascript:void(0);" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-original-title="Collapse" id="collapse-header">
                        <i className="ti ti-chevrons-up" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Filters Section */}
              <div className="card mb-3">
                <div className="card-body p-3">
                  <div className="row g-3 align-items-end">



                    {
                      hasFilterPermission && (
                        <div className="col-md-3">
                          <label className="form-label mb-1 small text-muted">Search by Name, ID</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search"
                            value={filterSearch}
                            onChange={(e) =>
                              setFilterSearch(e.target.value)}
                          />

                        </div>
                      )
                    }

                    {/* <div className="col-md-3">

                      <label className="form-label mb-1 small text-muted">Search by user name, user id</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search"
                        value={filterSearch}
                        onChange={(e) =>
                          setFilterSearch(e.target.value)}
                      />

                    </div> */}


                    {
                      hasFilterPermission && (
                        <div className="col-md-3">
                          <label className="form-label mb-1 small text-muted">Role</label>
                          <Select
                            classNamePrefix="react-select"
                            placeholder="Select Role"
                            options={roleOptions}
                            styles={{
                              container: (base) => ({ ...base, width: '100%' }),
                              control: (base) => ({ ...base, minHeight: '31px', height: '31px', fontSize: '13px' }),
                              valueContainer: (base) => ({ ...base, height: '31px', padding: '0 8px' }),
                              indicatorsContainer: (base) => ({ ...base, height: '31px' }),
                              menu: (base) => ({ ...base, fontSize: '13px' }),
                              option: (base) => ({ ...base, fontSize: '13px', padding: '6px 10px' }),
                            }}
                            value={roleOptions.find(opt => String(opt.value) === String(filterRole)) || roleOptions[0]}
                            onChange={(selected) => {
                              const val = selected ? selected.value : "";
                              setFilterRole(val);
                              if (!val) {
                                setFilterBranchId("");
                                setFilterDateFrom("");
                                setFilterDateTo("");
                                setFilterSearch("");
                                if (currentPage === 1) { fetchAuditLogs(1, true); } else { setCurrentPage(1); }
                              }
                            }}
                          />
                        </div>
                      )
                    }



                    {hasFilterPermission && (
                      <div className="col-md-3">
                        <label className="form-label mb-1 small text-muted">Branch</label>
                        <Select
                          classNamePrefix="react-select"
                          placeholder="Select Branch"
                          options={branchList}
                          styles={{
                            container: (base) => ({ ...base, width: '100%' }),
                            control: (base) => ({ ...base, minHeight: '31px', height: '31px', fontSize: '13px' }),
                            valueContainer: (base) => ({ ...base, height: '31px', padding: '0 8px' }),
                            indicatorsContainer: (base) => ({ ...base, height: '31px' }),
                            menu: (base) => ({ ...base, fontSize: '13px' }),
                            option: (base) => ({ ...base, fontSize: '13px', padding: '6px 10px' }),
                          }}
                          value={branchList.find(opt => String(opt.value) === String(filterBranchId)) || branchList[0]}
                          onChange={(selected) => {
                            const val = selected ? selected.value : "";
                            setFilterBranchId(val);
                            if (!val) {
                              setFilterRole("");
                              setFilterDateFrom("");
                              setFilterDateTo("");
                              setFilterSearch("");
                              if (currentPage === 1) { fetchAuditLogs(1, true); } else { setCurrentPage(1); }
                            }
                          }}
                        />
                      </div>
                    )}
                    <div className="col-md-3">
                      <label className="form-label mb-1 small text-muted">Date From</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label mb-1 small text-muted">Date To</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                      />
                    </div>
                    <div className="col-md-3">
                      <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-primary w-100" onClick={handleApplyFilters}>
                          Apply
                        </button>
                        <button className="btn btn-sm btn-outline-secondary w-100" onClick={handleClearFilters}>
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                  <BeatLoader />
                </div>
              ) : (
                <>
                  <div className="table-responsive custom-table">
                    <Table
                      className="table datanew dataTable no-footer"
                      pagination={false}
                      dataSource={logsData}
                      columns={columns}
                      rowKey="id"
                      scroll={{ x: "max-content" }}
                    />
                  </div>
                  <Pagination
                    totalRecords={totalRecords}
                    perPage={perPage}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Modal with Company Information */}
      {viewModalOpen && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg border-0">
              <div className="modal-header bg-light border-bottom">
                <h5 className="modal-title fw-bold">
                  <i className="ti ti-file-description me-2 text-primary"></i>
                  Audit Log Details
                </h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <div className="modal-body p-4">
                {viewLoading ? (
                  <div className="d-flex justify-content-center py-5">
                    <BeatLoader />
                  </div>
                ) : viewData ? (
                  <div className="row g-4">
                    {/* Audit log fields */}
                    <div className="col-md-6">
                      <div className="border rounded p-3 bg-light h-100">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">Log ID</label>
                        <p className="mb-0 fw-medium text-break">{viewData?.log_id || viewData?.id || "-"}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="border rounded p-3 bg-light h-100">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">Record ID</label>
                        <p className="mb-0 fw-medium">{viewData.record_id || "-"}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="border rounded p-3 bg-light h-100">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">User Name</label>
                        <p className="mb-0 fw-medium">{viewData?.user_name || "-"}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="border rounded p-3 bg-light h-100">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">User Role</label>
                        <p className="mb-0 fw-medium">{viewData?.role_name || "-"}</p>
                      </div>
                    </div>
                    {/* <div className="col-md-6">
                      <div className="border rounded p-3 bg-light h-100">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">Admin ID</label>
                        <p className="mb-0 fw-medium">{viewData.admin_id || "-"}</p>
                      </div>
                    </div> */}

                    {
                      viewData?.designation && (
                        <div className="col-md-6">
                          <div className="border rounded p-3 bg-light h-100">
                            <label className="text-muted small text-uppercase fw-semibold mb-1">Designation</label>
                            <p className="mb-0 fw-medium">{viewData?.designation || "-"}</p>
                          </div>
                        </div>
                      )
                    }

                    {
                      viewData?.department && (
                        <div className="col-md-6">
                          <div className="border rounded p-3 bg-light h-100">
                            <label className="text-muted small text-uppercase fw-semibold mb-1">Department</label>
                            <p className="mb-0 fw-medium">{viewData?.department || "-"}</p>
                          </div>
                        </div>
                      )
                    }

                    {
                      viewData?.branch_name && (
                        <div className="col-md-6">
                          <div className="border rounded p-3 bg-light h-100">
                            <label className="text-muted small text-uppercase fw-semibold mb-1">Branch</label>
                            <p className="mb-0 fw-medium">{viewData?.branch_name || "-"}</p>
                          </div>
                        </div>
                      )
                    }

                    <div className="col-md-6">
                      <div className="border rounded p-3 bg-light h-100">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">Module</label>
                        <p className="mb-0 fw-medium">{viewData.module || "-"}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="border rounded p-3 bg-light h-100">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">Action</label>
                        <p className="mb-0 fw-medium">{viewData.action || "-"}</p>
                      </div>
                    </div>
                    {/* <div className="col-md-6">
                      <div className="border rounded p-3 bg-light h-100">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">Record ID</label>
                        <p className="mb-0 fw-medium">{viewData.record_id || "-"}</p>
                      </div>
                    </div> */}
                    <div className="col-md-6">
                      <div className="border rounded p-3 bg-light h-100">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">Status</label>
                        <p className="mb-0">
                          <span className={`badge ${viewData.status === "SUCCESS" ? "bg-success" : "bg-danger"} px-3 py-2`}>
                            {viewData.status || "-"}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="border rounded p-3 bg-light h-100">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">IP Address</label>
                        <p className="mb-0 fw-medium font-monospace">{viewData.ip_address || "-"}</p>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="border rounded p-3 bg-light">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">Remarks</label>
                        <p className="mb-0">{viewData.remarks || "-"}</p>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="border rounded p-3 bg-light">
                        <label className="text-muted small text-uppercase fw-semibold mb-2 d-block border-bottom pb-1">
                          <i className="ti ti-database me-1 text-primary"></i> Data Record Details & Differences
                        </label>
                        <AuditDataViewer
                          oldData={viewData.old_data}
                          newData={viewData.new_data}
                          auditStatus={viewData.status}
                          action={viewData.action}
                        />
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="border rounded p-3 bg-light">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">User Agent</label>
                        <p className="mb-0 small text-secondary">{viewData.user_agent || "-"}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="border rounded p-3 bg-light h-100">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">Created At</label>
                        <p className="mb-0 fw-medium">{viewData.createdAt ? new Date(viewData.createdAt).toLocaleString() : "-"}</p>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="border rounded p-3 bg-light h-100">
                        <label className="text-muted small text-uppercase fw-semibold mb-1">Updated At</label>
                        <p className="mb-0 fw-medium">{viewData.updatedAt ? new Date(viewData.updatedAt).toLocaleString() : "-"}</p>
                      </div>
                    </div>

                    {/* Company Information Section */}
                    <div className="col-12">
                      <hr className="my-2" />
                      <h6 className="fw-bold text-primary">
                        <i className="ti ti-building me-2"></i>Company Information
                      </h6>
                    </div>
                    {viewData.user_info ? (
                      <>
                        <div className="col-md-6">
                          <div className="border rounded p-3 bg-light h-100">
                            <label className="text-muted small text-uppercase fw-semibold mb-1">Company Name</label>
                            <p className="mb-0 fw-medium">{viewData.user_info.company_name || "-"}</p>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="border rounded p-3 bg-light h-100">
                            <label className="text-muted small text-uppercase fw-semibold mb-1">Telephone</label>
                            <p className="mb-0 fw-medium">{viewData.user_info.company_telephone || "-"}</p>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="border rounded p-3 bg-light">
                            <label className="text-muted small text-uppercase fw-semibold mb-1">Address</label>
                            <p className="mb-0">{viewData.user_info.company_address || "-"}</p>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="border rounded p-3 bg-light h-100">
                            <label className="text-muted small text-uppercase fw-semibold mb-1">Logo</label>
                            <div>
                              {viewData.user_info.logo_path ? (
                                <img
                                  src={viewData.user_info.logo_path}
                                  alt="Logo"
                                  style={{ maxHeight: "50px", maxWidth: "100%", objectFit: "contain" }}
                                />
                              ) : (
                                "-"
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="border rounded p-3 bg-light h-100">
                            <label className="text-muted small text-uppercase fw-semibold mb-1">Favicon</label>
                            <div>
                              {viewData.user_info.favicon_apth ? (
                                <img
                                  src={viewData.user_info.favicon_apth}
                                  alt="Favicon"
                                  style={{ maxHeight: "40px", maxWidth: "100%", objectFit: "contain" }}
                                />
                              ) : (
                                "-"
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="col-12">
                        <div className="alert alert-info">No company information available.</div>
                      </div>
                    )}
                    {viewData.company_name && (
                      <div className="col-12">
                        <div className="border rounded p-3 bg-light">
                          <label className="text-muted small text-uppercase fw-semibold mb-1">Company Name (from log)</label>
                          <p className="mb-0 fw-medium">{viewData.company_name}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="alert alert-danger text-center">Failed to load details.</div>
                )}
              </div>
              <div className="modal-footer bg-light border-top">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditLogs;